import React, { useState } from "react";
import { Flex, Grid, Typography } from "antd";
import HButton from "../../../components/custom/input/button/Button";
import { Trigger, type TriggerType } from "../nodes/TriggerNode";

const { Text } = Typography;

const TRIGGER_OPTIONS: Array<{
    value: TriggerType;
    title: string;
    description: string;
    iconClassName: string;
}> = [
    {
        value: Trigger.Message.Inbound,
        title: "Incoming Trigger",
        description: "Trigger this rule when a message comes in.",
        iconClassName: "automationIconsSprite incoming-trigger-selection-icon",
    },
];

export interface TriggerTypeSelectionPanelProps {
    onConfirm: (triggerType: TriggerType) => void;
    onCancel: () => void;
    isCompact?: boolean;
}

export const TriggerTypeSelectionPanel: React.FC<TriggerTypeSelectionPanelProps> = ({
    onConfirm,
    onCancel,
    isCompact: compactFromParent,
}) => {
    const [selectedType, setSelectedType] = useState<TriggerType>(Trigger.Message.Inbound);
    const { md: greaterThanMediumScreen } = Grid.useBreakpoint();
    const isCompact = compactFromParent ?? !greaterThanMediumScreen;

    return (
        <div className="trigger-rule-config-panel trigger-type-selection-panel d-flex flex-column h-100">
            <Flex align="center" gap={1} className="hs-border-bottom-light trigger-rules-panel-header-bar px-3">
                <Text className="hs-fw-600 hs-fs-14 hs-color-BlackRussian">
                    Trigger
                </Text>
            </Flex>
            <div className="flex-fill p-3">
                <div>
                    <Text className="hs-fw-500 hs-fs-12 hs-color-BlackRussian">
                        Choose Message Direction
                    </Text>
                    <Text className="trigger-type-selection-panel-hint hs-fs-12 hs-color-mediumDark d-block mt-1 mb-2">
                        This choice is permanent for this flow.
                    </Text>
                    <div className="d-flex flex-column gap-2">
                        {TRIGGER_OPTIONS.map((opt) => (
                            <Flex
                                align="center"
                                gap={12}
                                key={opt.value}
                                className={`trigger-type-selection-panel-card rounded-4 p-3 ${
                                    selectedType === opt.value ? "trigger-type-selection-panel-card-selected" : ""
                                }`}
                                onClick={() => setSelectedType(opt.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        setSelectedType(opt.value);
                                    }
                                }}
                                role="button"
                                tabIndex={0}
                            >
                                <div className="trigger-type-selection-panel-icon d-flex align-items-center justify-content-center">
                                    <div
                                        className={`${opt.iconClassName} ${selectedType === opt.value ? "active" : ""}`}
                                    />
                                </div>
                                <div className="trigger-type-selection-panel-text d-flex flex-column gap-1">
                                    <span className="trigger-type-selection-panel-title">{opt.title}</span>
                                    <span className="trigger-type-selection-panel-desc">{opt.description}</span>
                                </div>
                            </Flex>
                        ))}
                    </div>
                </div>
            </div>
            <Flex
                className={
                    isCompact
                        ? "trigger-rule-config-panel-footer gap-3 flex-wrap"
                        : "trigger-rule-config-panel-footer justify-content-end gap-3 flex-wrap"
                }
                vertical={isCompact}
            >
                {isCompact ? (
                    <>
                        <HButton type="text" onClick={onCancel} buttonClassName="w-100">
                            Cancel
                        </HButton>
                        <HButton
                            type="primary"
                            onClick={() => onConfirm(selectedType)}
                            buttonClassName="w-100"
                        >
                            Confirm & Continue
                        </HButton>
                    </>
                ) : (
                    <HButton type="primary" onClick={() => onConfirm(selectedType)}>
                        Confirm & Continue
                    </HButton>
                )}
            </Flex>
        </div>
    );
};
